import moment from "moment";
import React, { useEffect, useRef, useState, useMemo } from "react";
import {
  Alert,
  Badge,
  Button,
  Pagination,
  Spinner,
  Table,
} from "flowbite-react";
import { FaArrowsLeftRight } from "react-icons/fa6";
import axios from "../../config/axiosInstance";
import { useQuery } from "react-query";
import { ADMIN, CATEGORIES, QUERIES } from "../../config/apiConfig";
import { formatToReal } from "../../utils/utils";
import { IoMdSearch } from "react-icons/io";
import AutoCompleteInput from "../inputs/Autocomplete";
import EmptyTable from "./EmptyTable";
import DrawerLoading from "../DrawerLoading";
import axiosInstance from "../../config/axiosInstance";
import { useApplication } from "../../contexts/ApplicationContext";
import TitleStripe from "../common/TitleStripe";
import ReactApexChart from "react-apexcharts";
import { LuFilter, LuFilterX } from "react-icons/lu";

/** ===========================
 * Pequenos utilitários visuais
 * =========================== */
function calcRate(success = 0, fail = 0) {
  const s = Number(success) || 0;
  const f = Number(fail) || 0;
  const total = s + f;
  return total > 0 ? Math.round((s / total) * 100) : 0;
}

function ProgressBar({
  success = 0,
  fail = 0,
  height = 10, // px
  showPercent = true, // exibe % junto
  labelPlacement = "auto", // "auto" | "inside" | "outside"
}) {
  const s = Number(success) || 0;
  const f = Number(fail) || 0;
  const total = s + f;
  const sPct = total > 0 ? (s / total) * 100 : 0;
  const fPct = total > 0 ? (f / total) * 100 : 0;

  // Em "auto": usa dentro apenas se ambos segmentos tiverem espaço suficiente.
  const canPlaceInside = sPct >= 20 && fPct >= 20;
  const placeInside =
    labelPlacement === "inside" ||
    (labelPlacement === "auto" && canPlaceInside);
  const placeOutside =
    labelPlacement === "outside" ||
    (labelPlacement === "auto" && !canPlaceInside);

  return (
    <div className="w-full">
      {/* Labels fora (nunca encavaladas) */}
      {placeOutside && total > 0 && (
        <div className="mb-1 flex items-center justify-between text-[11px] font-semibold">
          <span className="inline-flex items-center gap-1 text-emerald-700">
            <span className="h-2 w-2 rounded-full bg-emerald-500" />
            {s}
            {showPercent ? ` (${Math.round(sPct)}%)` : ""}
          </span>
          <span className="inline-flex items-center gap-1 text-rose-700">
            <span className="h-2 w-2 rounded-full bg-rose-500" />
            {f}
            {showPercent ? ` (${Math.round(fPct)}%)` : ""}
          </span>
        </div>
      )}

      {/* Barra segmentada */}
      <div
        className="bg-slate-200 rounded-full overflow-hidden relative"
        style={{ height }}
        role="progressbar"
        aria-valuemin={0}
        aria-valuemax={total}
        aria-valuenow={s}
        aria-label={`Sucessos: ${s}, Falhas: ${f}`}
        title={`Sucesso: ${s}${
          showPercent ? ` (${Math.round(sPct)}%)` : ""
        } · Falha: ${f}${showPercent ? ` (${Math.round(fPct)}%)` : ""}`}
      >
        <div className="flex h-full w-full">
          {/* Sucesso */}
          <div
            className="h-full bg-emerald-500 relative transition-[width] duration-300 ease-out"
            style={{ width: `${sPct}%` }}
          >
            {placeInside && (
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white/95">
                {s}
                {showPercent ? ` • ${Math.round(sPct)}%` : ""}
              </span>
            )}
          </div>

          {/* Falha */}
          <div
            className="h-full bg-rose-500 relative transition-[width] duration-300 ease-out"
            style={{ width: `${fPct}%` }}
          >
            {placeInside && (
              <span className="absolute inset-0 flex items-center justify-center text-[10px] font-semibold text-white/95">
                {f}
                {showPercent ? ` • ${Math.round(fPct)}%` : ""}
              </span>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

function Pill({ tone = "slate", children }) {
  const base = {
    slate: "bg-slate-100 text-slate-700",
    green: "bg-emerald-100 text-emerald-700",
    red: "bg-rose-100 text-rose-700",
    indigo: "bg-indigo-100 text-indigo-700",
    amber: "bg-amber-100 text-amber-700",
  }[tone];
  return (
    <span
      className={`inline-flex items-center gap-1 px-2.5 py-1 text-[11px] font-medium rounded-full ${base}`}
    >
      {children}
    </span>
  );
}

/** Overlay de carregamento para a área principal */
function LoadingOverlay({ visible, label = "Carregando..." }) {
  if (!visible) return null;
  return (
    <div className="absolute inset-0 z-20 flex flex-col items-center justify-center gap-3 bg-white/60 backdrop-blur-sm">
      <Spinner size="lg" />
      <div className="text-sm font-medium text-gray-700">{label}</div>
      <div className="w-40 h-1.5 rounded-full bg-gray-200 overflow-hidden">
        <div className="h-full w-1/3 animate-[progress_1.4s_ease-in-out_infinite] bg-gray-400 rounded-full" />
      </div>
      <style>{`
        @keyframes progress {
          0% { transform: translateX(-120%); }
          50% { transform: translateX(60%); }
          100% { transform: translateX(140%); }
        }
      `}</style>
    </div>
  );
}

const AdminHistoryTableProviders = ({ isActive = true }) => {
  const { userPermission, user } = useApplication();
  const [activeTab, setActiveTab] = useState(1);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const onPageChange = (page) => setCurrentPage(page);
  const [searchName, setSearchName] = useState("");
  const [nameParam, setNameParam] = useState("");
  const [totalPriceSum, setTotalPriceSum] = useState(0);
  const [totalCount, setTotalCount] = useState(0);
  const reportRef = useRef(null);
  const [chartSeries, setChartSeries] = useState([]);
  const [chartCategories, setChartCategories] = useState([]);
  const [insumoChartSeries, setInsumoChartSeries] = useState([]);
  const [insumoChartCategories, setInsumoChartCategories] = useState([]);
  const isRepresentativeUser = user?.Company?.companyTypeId === "REPRESENTANTE";

  const [selectedProvider, setSelectedProvider] = useState({
    label: "Todos os provedores",
    value: "",
  });
  const [selectedStatuses, setSelectedStatuses] = useState([]);
  const [startDate, setStartDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [endDate, setEndDate] = useState(
    moment(new Date()).format("YYYY-MM-DD")
  );
  const [loadingDownload, setLoadingDownload] = useState(null);
  const [activeIndexLoading, setActiveIndexLoading] = useState(null);
  const [showFilters, setShowFilters] = useState(true);
  const [renderFilters, setRenderFilters] = useState(false);

  const [selectedRepresentative, setSelectedRepresentative] = useState({
    label: "Todos os representantes",
    value: 0,
  });

  const [selectedCustomer, setSelectedCustomer] = useState({
    label: "Todos os clientes",
    value: 0,
  });

  // Accordion state
  const [openProviders, setOpenProviders] = useState({});
  const toggleProvider = (providerId) =>
    setOpenProviders((prev) => ({ ...prev, [providerId]: !prev[providerId] }));
  const expandAll = (list) => {
    const map = {};
    (list || []).forEach((p) => (map[p.providerId] = true));
    setOpenProviders(map);
  };
  const collapseAll = (list) => {
    const map = {};
    (list || []).forEach((p) => (map[p.providerId] = false));
    setOpenProviders(map);
  };

  const {
    isLoading: isLoadingReportSimplifiedQueries,
    error: errorListReportSimplifiedQueries,
    data: listReportSimplifiedQueries,
  } = useQuery(
    [
      "listReportSimplifiedQueries",
      startDate,
      endDate,
      selectedProvider,
      selectedRepresentative,
      selectedCustomer,
    ],
    async () => {
      const { data } = await axios.get(
        `${
          ADMIN.GET_QUERY_PROVIDER_USAGE
        }?startDate=${startDate}&endDate=${endDate}&providerId=${
          selectedProvider?.value || ""
        }&representativeId=${selectedRepresentative?.value}&customerId=${
          selectedCustomer?.value
        }`
      );
      return data.data;
    },
    { enabled: activeTab === 1 && isActive }
  );

  // Filtro: mostrar apenas consultas com saída (sucesso ou falha) e ocultar provedores sem movimento
  const filteredProviders = useMemo(() => {
    if (!Array.isArray(listReportSimplifiedQueries)) return [];

    const base = selectedProvider?.value
      ? listReportSimplifiedQueries.filter(
          (p) => p.providerId === selectedProvider.value
        )
      : listReportSimplifiedQueries;

    const mapped = base.map((p) => ({
      ...p,
      queries: (p.queries || []).filter(
        (q) => (Number(q?.successCount) || 0) + (Number(q?.failCount) || 0) > 0
      ),
    }));

    return mapped.filter((p) => p.queries && p.queries.length > 0);
  }, [listReportSimplifiedQueries, selectedProvider]);

  useEffect(() => {
    if (showFilters) setRenderFilters(true);
    else {
      const t = setTimeout(() => setRenderFilters(false), 500);
      return () => clearTimeout(t);
    }
  }, [showFilters]);

  useEffect(() => {
    if (isRepresentativeUser) {
      const label =
        user?.Company?.name || user?.Company?.socialReason || "Representante";
      const value = user?.Company?.id || 0;
      setSelectedRepresentative({ label, value });
      return;
    }

    setSelectedRepresentative({
      label: "Todos os representantes",
      value: 0,
    });
  }, [isRepresentativeUser, user]);

  const {
    isLoading: isLoadingProviders,
    error: errorListProviders,
    data: providersRaw, // <- agora vem o array completo do backend
  } = useQuery(
    ["listProviders"],
    async () => {
      const { data } = await axios.get(`${ADMIN.GET_PROVIDERS}`);
      // aqui retornamos o array original (o que tem associatedQueries)
      return data.data?.data || [];
    },
    { enabled: isActive && !isRepresentativeUser }
  );

  // options para o AutoComplete
  const providerOptions = useMemo(() => {
    if (!Array.isArray(providersRaw)) {
      return [{ label: "Todos os provedores", value: "" }];
    }

    let providers = providersRaw.map((p) => ({
      label: p.name,
      value: p.id,
    }));

    return [{ label: "Todos os provedores", value: "" }, ...providers];
  }, [providersRaw]);

  // Mapa: "providerId-queryId" -> preço unitário (QueryProvider.price)
  const providerPriceMap = useMemo(() => {
    const map = {};

    if (!Array.isArray(providersRaw)) return map;

    providersRaw.forEach((prov) => {
      (prov.associatedQueries || []).forEach((q) => {
        const key = `${prov.id}-${q.id}`; // prov.id = providerId, q.id = queryId
        const unitPrice = Number(q?.QueryProvider?.price) || 0;
        map[key] = unitPrice;
      });
    });

    return map;
  }, [providersRaw]);

  const providerLegend = useMemo(() => {
    if (!Array.isArray(filteredProviders)) return [];
    return filteredProviders.map((provider, idx) => ({
      id: provider.providerId,
      name: `Provedor ${idx + 1}`,
    }));
  }, [filteredProviders]);

  const providerColors = useMemo(() => {
    if (!Array.isArray(filteredProviders) || filteredProviders.length === 0) {
      return ["#2563eb"];
    }
    const total = filteredProviders.length;
    return Array.from({ length: total }, (_, i) => {
      const hue = Math.round((i * 137.508) % 360);
      const lightness = 45 + (i % 3) * 10;
      return `hsl(${hue}, 70%, ${lightness}%)`;
    });
  }, [filteredProviders]);

  const {
    isLoading: isLoadingListRepresentatives,
    error: errorListRepresentatives,
    data: listRepresentatives,
  } = useQuery(
    ["listRepresentatives"],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_REPRESENTATIVES}?itemsPerPage=-1`
      );

      const representatives = [
        { label: "Todos os representantes", value: "" },
        {
          label: user?.Company?.name || user?.Company?.socialReason,
          value: user?.companyId,
        },
        ...data.data.data.map((representative) => ({
          value: representative.id,
          label: representative?.name || representative?.socialReason,
        })),
      ];

      return representatives;
    },
    {
      enabled: isActive && !isRepresentativeUser,
    }
  );

  const {
    isLoading: isLoadingCustomers,
    error: errorListCustomers,
    data: listCustomers,
  } = useQuery(
    ["listCustomers", selectedRepresentative],
    async () => {
      try {
        const { data } = await axios.get(
          `${ADMIN.GET_CUSTOMERS}?itemsPerPage=-1&representativeId=${
            selectedRepresentative?.value || ""
          }`
        );

        if (!data?.data?.data) {
          throw new Error("Erro ao buscar clientes");
        }

        let customers = data.data.data.map((customer) => ({
          label:
            customer.typeCustomer === "CPF"
              ? customer.name
              : customer.socialReason,
          value: customer.id,
          vcm: customer.vcm,
          typePayment: customer.typePayment,
          credit: customer.credit,
        }));

        customers = [{ label: "Todos os clientes", value: 0 }, ...customers];

        return customers;
      } catch (error) {
        throw new Error(`Erro ao buscar clientes: ${error.message}`);
      }
    },
    { enabled: isActive }
  );

  const {
    isLoading: isLoadingReportQueries,
    error: errorListReportQueries,
    data: listReportQueries,
  } = useQuery(
    [
      "listReportQueries",
      startDate,
      endDate,
      currentPage,
      selectedStatuses,
      selectedProvider,
      nameParam,
      selectedCustomer,
      selectedRepresentative,
    ],
    async () => {
      const { data } = await axios.get(
        `${ADMIN.GET_COMPANY_QUERIES_HISTORY}/${
          selectedCustomer?.value
        }?startDate=${startDate}&endDate=${endDate}&page=${currentPage}&status=${selectedStatuses}&queries=&search=${nameParam}&providerId=${
          selectedProvider?.value || ""
        }&isModular=${true}&representativeId=${selectedRepresentative?.value}`
      );
      setTotalPages(data.data.totalPages);
      setTotalPriceSum(data.data.totalPriceSum);
      setTotalCount(data.data.totalCount);
      return data.data.data;
    },
    { enabled: activeTab === 0 && isActive }
  );

  // Monta dados do gráfico a partir dos providers filtrados
  useEffect(() => {
    if (activeTab !== 1) return;
    const list = filteredProviders;
    if (!Array.isArray(list)) return;

    const categories = [];
    const successData = [];
    const failData = [];

    if (selectedProvider?.value) {
      const selected = list[0];
      if (selected) {
        selected.queries?.forEach((q) => {
          categories.push(q.queryName);
          successData.push(q.successCount);
          failData.push(q.failCount);
        });
      }
    } else {
      list.forEach((provider) => {
        categories.push(provider.providerName);
        let success = 0;
        let fail = 0;
        provider.queries?.forEach((q) => {
          success += q.successCount || 0;
          fail += q.failCount || 0;
        });
        successData.push(success);
        failData.push(fail);
      });
    }

    setChartCategories(categories);
    setChartSeries([
      { name: "Sucesso", data: successData },
      { name: "Falha", data: failData },
    ]);
  }, [filteredProviders, activeTab, selectedProvider]);

  useEffect(() => {
    if (activeTab !== 1) {
      setInsumoChartSeries([]);
      setInsumoChartCategories([]);
      return;
    }

    if (!Array.isArray(filteredProviders) || filteredProviders.length === 0) {
      setInsumoChartSeries([]);
      setInsumoChartCategories([]);
      return;
    }

    const aggregated = new Map();

    filteredProviders.forEach((provider) => {
      (provider.queries || []).forEach((query) => {
        const key = `${query.queryId ?? ""}-${query.queryName ?? ""}`;
        if (!aggregated.has(key)) {
          aggregated.set(key, {
            queryName: query.queryName || "Produto sem nome",
            perProviderCount: {},
            totalSuccess: 0,
            totalFail: 0,
            total: 0,
          });
        }

        const sc = Number(query.successCount) || 0;
        const fc = Number(query.failCount) || 0;
        const entry = aggregated.get(key);
        entry.perProviderCount[provider.providerId] =
          (entry.perProviderCount[provider.providerId] || 0) + sc;
        entry.totalSuccess += sc;
        entry.totalFail += fc;
        entry.total += sc + fc;
      });
    });

    const aggregatedEntries = Array.from(aggregated.values());
    if (aggregatedEntries.length === 0) {
      setInsumoChartCategories([]);
      setInsumoChartSeries([]);
      return;
    }

    aggregatedEntries.sort((a, b) => b.total - a.total);

    const categories = aggregatedEntries.map((item) => item.queryName);

    const series = providerLegend.map((provider) => ({
      name: provider.name,
      data: aggregatedEntries.map((item) => ({
        x: item.queryName,
        y: Number(item.perProviderCount[provider.id]) || 0,
        totalSuccess: Number(item.totalSuccess) || 0,
        totalFail: Number(item.totalFail) || 0,
      })),
    }));

    setInsumoChartCategories(categories);
    setInsumoChartSeries(series);
  }, [filteredProviders, activeTab, providerLegend]);

  const handleCheckboxChange = (status, setState) => {
    setState((prev) =>
      prev.includes(status)
        ? prev.filter((i) => i !== status)
        : [...prev, status]
    );
  };

  const handleSearchName = (e) => {
    e.preventDefault();
    setNameParam(searchName);
  };

  useEffect(() => {
    setCurrentPage(1);
  }, [activeTab]);

  const handleDownloadDetails = async (
    index,
    fileName,
    queryId,
    data,
    parameter
  ) => {
    setLoadingDownload(true);
    setActiveIndexLoading(index);
    try {
      const dataFile = await axiosInstance.post(QUERIES.GENERATE_PDF, {
        parameter,
        queryId,
        data: JSON.stringify(data),
        token: localStorage.getItem("token"),
      });
      const url = dataFile.data.data;
      if (url) {
        const a = document.createElement("a");
        a.href = url;
        a.setAttribute("target", "_blank");
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
    } catch (error) {
      console.error("Erro ao iniciar o download:", error);
    } finally {
      setLoadingDownload(false);
      setActiveIndexLoading(null);
    }
  };

  /** Visibilidade do overlay global de conteúdo */
  const showMainOverlay =
    (activeTab === 0 && isLoadingReportQueries) ||
    (activeTab === 1 && isLoadingReportSimplifiedQueries);

  /** ===========================
   * Layout
   * =========================== */
  return (
    <div>
      <div className="flex justify-between items-center mb-2">
        <Button
          size="xs"
          onClick={() => setShowFilters((prev) => !prev)}
          className="primary text-white transition-all duration-300"
        >
          <div className="flex items-center gap-2 transition-all duration-300">
            <span>{showFilters ? "Esconder filtros" : "Mostrar filtros"}</span>
            <span className={`transition-transform duration-300`}>
              {!showFilters ? <LuFilter /> : <LuFilterX />}
            </span>
          </div>
        </Button>
      </div>

      <div className="flex flex-col gap-4">
        {showFilters && (
          <div className="transition-all duration-500 ease-in-out overflow-hidden bg-white rounded-md shadow-sm border border-gray-200 p-4 relative">
            {renderFilters && (
              <div className="transition-opacity duration-500 min-w-[200px]">
                {isLoadingProviders && <DrawerLoading />}
                <div className="title-syncx">Filtro avançado</div>
                <div className="flex flex-col md:flex-row gap-3 md:gap-10">
                  {!isRepresentativeUser && (
                    <div className="mt-4 min-w-[250px]">
                      <AutoCompleteInput
                        options={providerOptions}
                        label="Provedor"
                        onChange={(provider) => setSelectedProvider(provider)}
                        value={selectedProvider?.value}
                        isLoading={isLoadingProviders}
                      />
                      {errorListProviders && (
                        <Alert color="failure" className="mt-2">
                          Ocorreu um erro ao buscar provedores
                        </Alert>
                      )}
                    </div>
                  )}
                  <div>
                    <div className="font-semibold mt-1 text-sm mb-2">
                      Período
                    </div>
                    <div className="flex items-center gap-1">
                      <input
                        type="date"
                        value={startDate}
                        onChange={(e) => {
                          setStartDate(e.target.value);
                          if (moment(e.target.value).isAfter(moment(endDate))) {
                            setEndDate(e.target.value);
                          }
                        }}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none focus:ring-0"
                      />
                      <div className="text-xs">Até</div>
                      <input
                        type="date"
                        value={endDate}
                        onChange={(e) => setEndDate(e.target.value)}
                        min={startDate}
                        className="w-full block py-3 md:py-2 px-2 rounded-md text-xs text-gray-600 bg-gray-100 border-0 focus:outline-none focus:ring-0"
                      />
                    </div>
                  </div>
                  {!isRepresentativeUser && (
                    <div className="flex flex-col">
                      <label className="text-sm font-medium text-gray-700 mb-1">
                        Representante
                      </label>
                      <div className="h-11">
                        <AutoCompleteInput
                          options={listRepresentatives}
                          label=""
                          onChange={(representative) => {
                            setSelectedRepresentative(representative);
                            setSelectedCustomer({
                              label: "Todos os clientes",
                              value: 0,
                            });
                          }}
                          value={selectedRepresentative?.value}
                          isLoading={isLoadingListRepresentatives}
                          className="h-full"
                        />
                      </div>
                      {errorListRepresentatives && (
                        <Alert color="failure" className="mt-2">
                          Ocorreu um erro ao buscar representantes
                        </Alert>
                      )}
                    </div>
                  )}

                  {/* Cliente */}
                  <div className="flex flex-col">
                    <label className="text-sm font-medium text-gray-700 mb-1">
                      Cliente
                    </label>
                    <div className="h-11">
                      <AutoCompleteInput
                        options={listCustomers}
                        label=""
                        onChange={(customer) => setSelectedCustomer(customer)}
                        value={selectedCustomer?.value}
                        isLoading={isLoadingCustomers}
                        className="h-full"
                      />
                    </div>
                    {errorListCustomers && (
                      <Alert color="failure" className="mt-2">
                        Ocorreu um erro ao buscar clientes
                      </Alert>
                    )}
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Conteúdo principal com overlay */}
        <div className="relative">
          <LoadingOverlay
            visible={showMainOverlay}
            label={
              activeTab === 0
                ? "Carregando histórico..."
                : "Carregando resumo por provedor..."
            }
          />

          <div
            className={`flex-1 overflow-x-scroll print-padding print-content ${
              showMainOverlay
                ? "pointer-events-none select-none opacity-60"
                : ""
            }`}
            ref={reportRef}
          >
            {activeTab === 0 && (
              <form
                className="flex gap-1 mt-0 w-full pl-3 pr-2 py-2 md:py-1 rounded-md shadow-sm bg-gray-300"
                onSubmit={handleSearchName}
              >
                <input
                  type="text"
                  placeholder="Busque por parâmetro da consulta..."
                  className="border-0 py-1 text-sm mt-1 focus:outline-none focus:ring-0 px-0 w-full bg-gray-300 placeholder:text-gray-700"
                  onChange={(e) => setSearchName(e.target.value)}
                  value={searchName}
                />
                <Button
                  size="sm"
                  className="flex items-center bg-secondary w-20"
                  type="submit"
                >
                  <IoMdSearch />
                </Button>
              </form>
            )}

            <small
              className="gap-2 items-center mt-5 mb-1 flex md:hidden justify-center"
              style={{ fontSize: 10 }}
            >
              <span>
                Deslize para o lado para visualizar todas as informações
              </span>{" "}
              <FaArrowsLeftRight />
            </small>

            {activeTab === 0 ? (
              <>
                {listReportQueries?.length > 0 || isLoadingReportQueries ? (
                  <div className="overflow-x-auto shadow-md rounded-lg mt-0 md:mt-5">
                    <Table striped style={{ fontSize: "13px" }}>
                      <Table.Head>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Data
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Insumo
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Parâmetro
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Usuário
                        </Table.HeadCell>
                        <Table.HeadCell className="text-center bg-gray-300">
                          Status
                        </Table.HeadCell>
                      </Table.Head>
                      <Table.Body className="divide-y">
                        {!isLoadingReportQueries
                          ? listReportQueries?.map((query, index) => (
                              <Table.Row
                                key={index}
                                className="bg-white dark:border-gray-700 dark:bg-gray-800"
                              >
                                <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                  {moment(query.createdAt).format(
                                    "DD/MM/yyyy | HH:mm"
                                  )}
                                </Table.Cell>
                                <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                  {query.queryName}
                                </Table.Cell>
                                <Table.Cell className="font-medium text-gray-900 dark:text-white text-center py-2">
                                  {query.parameter}
                                </Table.Cell>
                                <Table.Cell className="text-center py-2">
                                  {query.userName}
                                </Table.Cell>
                                <Table.Cell className="text-center py-2">
                                  {query.isSuccess ? (
                                    <Badge
                                      color="success"
                                      className="w-fit mx-auto text-xs"
                                    >
                                      Sucesso
                                    </Badge>
                                  ) : (
                                    <Badge
                                      color="failure"
                                      className="w-fit mx-auto text-xs"
                                    >
                                      Falha
                                    </Badge>
                                  )}
                                </Table.Cell>
                              </Table.Row>
                            ))
                          : // Skeleton simples
                            Array.from({ length: 6 }).map((_, i) => (
                              <Table.Row key={i}>
                                {Array.from({ length: 5 }).map((__, j) => (
                                  <Table.Cell key={j}>
                                    <div className="h-3 w-full bg-gray-200 rounded animate-pulse" />
                                  </Table.Cell>
                                ))}
                              </Table.Row>
                            ))}
                      </Table.Body>
                    </Table>
                  </div>
                ) : (
                  <EmptyTable icon="search_off">
                    Nenhum registro encontrado com esses filtros
                  </EmptyTable>
                )}
              </>
            ) : (
              <>
                {/* ======== Resumo geral por provedor ======== */}
                {!isRepresentativeUser && chartSeries.length > 0 && (
                  <div className="mb-8 mt-4">
                    <TitleStripe>Resumo geral por provedor</TitleStripe>
                    <div className="overflow-x-auto bg-gray-100 mt-4 rounded-lg p-4">
                      <ReactApexChart
                        options={{
                          chart: {
                            type: "bar",
                            stacked: true,
                            toolbar: { show: false },
                            zoom: { enabled: true },
                          },
                          colors: ["#22c55e", "#ef4444"],
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              borderRadius: 8,
                              dataLabels: {
                                total: {
                                  enabled: true,
                                  style: { fontSize: "13px", fontWeight: 900 },
                                },
                              },
                            },
                          },
                          xaxis: { categories: chartCategories },
                          legend: { position: "top", offsetY: 10 },
                          fill: { opacity: 1 },
                        }}
                        series={chartSeries}
                        type="bar"
                        height={350}
                      />
                    </div>
                  </div>
                )}

                {insumoChartSeries.length > 0 && (
                  <div className="mb-8 mt-4">
                    <TitleStripe>Resumo geral por insumo</TitleStripe>
                    <div className="overflow-x-auto bg-gray-100 mt-4 rounded-lg p-4">
                      <ReactApexChart
                        options={{
                          chart: {
                            type: "bar",
                            stacked: true,
                            toolbar: { show: false },
                            zoom: { enabled: true },
                          },
                          colors: providerColors,
                          plotOptions: {
                            bar: {
                              horizontal: false,
                              borderRadius: 8,
                              dataLabels: {
                                total: {
                                  enabled: true,
                                  style: { fontSize: "13px", fontWeight: 900 },
                                },
                              },
                            },
                          },
                          xaxis: { categories: insumoChartCategories },
                          yaxis: {
                            labels: {
                              formatter: (value) =>
                                Number(value || 0).toLocaleString("pt-BR"),
                            },
                          },
                          tooltip: {
                            custom: ({ series, seriesIndex, dataPointIndex, w }) => {
                              const seriesName =
                                w?.config?.series?.[seriesIndex]?.name || "";
                              const dataPoint =
                                w?.config?.series?.[seriesIndex]?.data?.[
                                  dataPointIndex
                                ] || {};
                              const qty =
                                series?.[seriesIndex]?.[dataPointIndex] || 0;
                              const category =
                                dataPoint?.x ||
                                w?.globals?.labels?.[dataPointIndex] ||
                                "";
                              const totalSuccess = Number(
                                dataPoint?.totalSuccess || 0
                              );
                              const totalFail = Number(
                                dataPoint?.totalFail || 0
                              );

                              return `
                                <div class="p-2 text-xs">
                                  <div class="font-semibold text-slate-800">${seriesName}</div>
                                  <div class="text-slate-600">${category}</div>
                                  <div class="mt-1">Qtd: <strong>${Number(
                                    qty
                                  ).toLocaleString("pt-BR")}</strong></div>
                                  <div class="mt-1">Total sucesso: <strong>${totalSuccess.toLocaleString(
                                    "pt-BR"
                                  )}</strong></div>
                                  <div>Total falha: <strong>${totalFail.toLocaleString(
                                    "pt-BR"
                                  )}</strong></div>
                                </div>
                              `;
                            },
                          },
                          legend: { position: "top", offsetY: 10 },
                          fill: { opacity: 1 },
                        }}
                        series={insumoChartSeries}
                        type="bar"
                        height={350}
                      />
                    </div>
                  </div>
                )}

                {/* ======== Cards/Accordion por provedor ======== */}
                {!isLoadingReportSimplifiedQueries &&
                  Array.isArray(filteredProviders) &&
                  !isRepresentativeUser && (
                    <div className="space-y-4">
                      {/* Controles globais */}
                      <div className="flex items-center justify-end gap-2">
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => expandAll(filteredProviders)}
                        >
                          Expandir todos
                        </Button>
                        <Button
                          size="xs"
                          color="gray"
                          onClick={() => collapseAll(filteredProviders)}
                        >
                          Recolher todos
                        </Button>
                      </div>

                      {filteredProviders.map((provider, indexP) => {
                        const totals = provider.queries?.reduce(
                          (acc, q) => {
                            const sc = Number(q?.successCount) || 0;
                            const fc = Number(q?.failCount) || 0;

                            // pega o preço correto usando providerId + queryId
                            const key = `${provider.providerId}-${q.queryId}`;
                            const unitPrice = Number(
                              providerPriceMap[key] || 0
                            );

                            acc.success += sc;
                            acc.fail += fc;
                            acc.revenue += sc * unitPrice; // só sucesso gera custo/receita
                            return acc;
                          },
                          { success: 0, fail: 0, revenue: 0 }
                        );

                        const successRate = calcRate(
                          totals.success,
                          totals.fail
                        );
                        const successRateTone =
                          successRate < 50
                            ? "red"
                            : successRate < 80
                            ? "amber"
                            : "green";
                        const isOpen = !!openProviders[provider.providerId];

                        return (
                          <div
                            key={indexP}
                            className="rounded-2xl border border-slate-200 bg-white shadow-sm overflow-hidden"
                          >
                            {/* Cabeçalho (botão) */}
                            <button
                              type="button"
                              onClick={() =>
                                toggleProvider(provider.providerId)
                              }
                              className="w-full p-4 md:p-5 flex items-center justify-between hover:bg-slate-50"
                            >
                              <div className="flex items-center gap-3">
                                <div className="h-10 w-10 rounded-xl bg-indigo-100 text-indigo-700 grid place-content-center font-bold">
                                  {provider?.providerName?.[0] || "P"}
                                </div>
                                <div>
                                  <div className="text-sm text-slate-500">
                                    Provedor
                                  </div>
                                  <div className="text-base md:text-lg font-semibold text-slate-800">
                                    {provider?.providerName}
                                  </div>
                                </div>
                              </div>

                              <div className="grid grid-cols-2 md:grid-cols-4 gap-2 md:gap-4 items-center">
                                <div className="min-w-[120px]">
                                  <div className="text-[11px] text-slate-500">
                                    Sucessos
                                  </div>
                                  <div className="text-sm md:text-base font-semibold text-emerald-600">
                                    {totals.success}
                                  </div>
                                </div>
                                <div className="min-w-[120px]">
                                  <div className="text-[11px] text-slate-500">
                                    Falhas
                                  </div>
                                  <div className="text-sm md:text-base font-semibold text-rose-600">
                                    {totals.fail}
                                  </div>
                                </div>
                                <div className="min-w-[120px]">
                                  <div className="text-[11px] text-slate-500">
                                    Taxa de sucesso
                                  </div>
                                  <div className="mt-1">
                                    <Pill tone={successRateTone}>
                                      {successRate}% sucesso
                                    </Pill>
                                  </div>
                                </div>
                                <div className="min-w-[120px] hidden md:block">
                                  <div className="text-[11px] text-slate-500">
                                    Receita
                                  </div>
                                  <div className="text-sm md:text-base font-semibold text-slate-800">
                                    {formatToReal(totals.revenue)}
                                  </div>
                                </div>
                              </div>
                            </button>

                            {/* Barra de progresso geral */}
                            <div className="px-4 md:px-5 pb-3">
                              <ProgressBar
                                success={totals.success}
                                fail={totals.fail}
                              />
                            </div>

                            {/* Detalhes expandíveis */}
                            {isOpen && (
                              <div className="p-4 md:p-5 bg-slate-50/50 border-t border-slate-100">
                                <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3 md:gap-4">
                                  {provider?.queries?.map((q, idx) => {
                                    const sc = Number(q?.successCount) || 0;
                                    const fc = Number(q?.failCount) || 0;

                                    const key = `${provider.providerId}-${q.queryId}`;
                                    const unitPrice = Number(
                                      providerPriceMap[key] || 0
                                    );

                                    const revenue = sc * unitPrice;
                                    const rate = calcRate(sc, fc);
                                    const rateTone =
                                      rate < 50
                                        ? "red"
                                        : rate < 80
                                        ? "amber"
                                        : "green";

                                    return (
                                      <div
                                        key={idx}
                                        className="rounded-xl border border-slate-200 bg-white p-3 md:p-4 shadow-sm"
                                      >
                                        <div className="flex items-start justify-between gap-3">
                                          <div>
                                            <div className="text-[11px] text-slate-500">
                                              Produto
                                            </div>
                                            <div className="text-sm font-semibold text-slate-800 leading-tight">
                                              {q?.queryName}
                                            </div>
                                            {/* opcional: mostrar preço unitário */}
                                            <div className="mt-1 text-[11px] text-slate-500">
                                              Preço unitário:{" "}
                                              {formatToReal(unitPrice)}
                                            </div>
                                          </div>
                                          <Pill tone={rateTone}>
                                            {rate}% sucesso
                                          </Pill>
                                        </div>

                                        <div className="mt-3 flex items-center gap-3">
                                          <Pill tone="green">{sc} OK</Pill>
                                          <Pill tone="red">{fc} FALHA</Pill>
                                          <Pill tone="indigo">
                                            {formatToReal(revenue)}
                                          </Pill>
                                        </div>

                                        <div className="mt-3">
                                          <ProgressBar success={sc} fail={fc} />
                                        </div>
                                      </div>
                                    );
                                  })}
                                </div>
                              </div>
                            )}
                          </div>
                        );
                      })}
                    </div>
                  )}
              </>
            )}

            {totalPages > 1 && activeTab === 0 && (
              <div className="flex overflow-x-auto justify-center mt-3">
                <Pagination
                  layout="pagination"
                  currentPage={currentPage}
                  totalPages={totalPages}
                  onPageChange={onPageChange}
                  previousLabel=""
                  nextLabel=""
                  showIcons
                />
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminHistoryTableProviders;
